import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslate } from 'react-admin';

import {
  RecordsQuery,
  RedemptionRecordPage,
  SubscriptionRecordPage,
  listRedemptionRecords,
  listSubscriptionRecords,
} from 'src/pages/admin/services/vaultService';
import { parseApiError } from 'src/pages/admin/utils/decimal-input';

import type { RecordTab } from './constants';
import { recordsErrorMessage } from './helpers';

export type RecordsPage = SubscriptionRecordPage | RedemptionRecordPage;

/**
 * `GET /vault/records/:tab` for one page. Nothing is read without a query (the vault list is still resolving). `key`
 * names the tab and filters the page belongs to. The page and the error are stored with the key they were read under
 * and returned only while it is still the key — decided in the render itself, not in an effect, so the first render
 * after a tab switch never hands the other tab's rows to this tab's table (that crashed RedemptionsTable). A response
 * that lands after the key changed is dropped as well.
 */
export const useRecords = (tab: RecordTab, query: RecordsQuery | null, key: string) => {
  const translate = useTranslate();
  // react-admin's translate is not identity-stable; a ref keeps refetch stable (see useVaults).
  const translateRef = useRef(translate);
  translateRef.current = translate;
  const latest = useRef(0);
  const [stored, setStored] = useState<{ key: string; page: RecordsPage | null; error: string | null } | null>(null);
  const [loading, setLoading] = useState(false);
  // The query is a fresh object each render; its JSON is what decides whether to read again.
  const queryJson = query ? JSON.stringify(query) : null;

  const refetch = useCallback(async () => {
    if (!queryJson) return;
    const q = JSON.parse(queryJson) as RecordsQuery;
    latest.current += 1;
    const call = latest.current;
    setLoading(true);
    try {
      const result = tab === 'subscriptions' ? await listSubscriptionRecords(q) : await listRedemptionRecords(q);
      if (call !== latest.current) return;
      setStored({ key, page: result, error: null });
    } catch (error) {
      if (call !== latest.current) return;
      setStored({ key, page: null, error: recordsErrorMessage(parseApiError(error), translateRef.current) });
    } finally {
      if (call === latest.current) setLoading(false);
    }
  }, [tab, queryJson, key]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  const current = stored && stored.key === key ? stored : null;
  return { page: current?.page ?? null, loading, loadError: current?.error ?? null, refetch };
};
